import { get, post } from 'infras/rest_client';
import { ViewerItem, UserListItem } from 'presentations/api/schema/user';
import { ProjectUserCreateBody } from 'presentations/api/schema/project_user';
import { apiPath } from 'presentations/routes/path';
import { SyntheticEvent, useEffect, useState } from 'react';
import { TeamUserListItem } from 'presentations/api/schema/team_user';
import { ProjectItem } from 'presentations/api/schema/project';
import { useDebounce } from 'presentations/views/hooks/debounce';

interface Props {
  projectUid: string;
  viewer: ViewerItem;
  onRequested: (newUsers: UserListItem[]) => void;
  project?: ProjectItem;
}

export function useMemberInviteDialog(props: Props) {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [selectedUsers, setSelectedUsers] = useState<UserListItem[]>([]);
  const [suggestTeamUsers, setSuggestTeamUsers] = useState<TeamUserListItem[]>(
    [],
  );
  const [inputValue, setInputValue] = useState<string>('');
  const debouncedInputValue = useDebounce(inputValue, 200);

  useEffect(() => {
    (async () => {
      // ここでAPIからリアルタイムにTeamUserを検索する
      const findResult = await get(
        apiPath(`/v1/teams/${props.viewer.team?.uid}/users`, {
          keyword: debouncedInputValue,
          page: 1,
          perPage: 20,
        }),
      );
      if (!findResult.isSuccess) {
        return;
      }

      const teamUsers: TeamUserListItem[] = findResult.data;
      setSuggestTeamUsers(teamUsers);
    })();
  }, [debouncedInputValue]);

  const onClose = () => {
    setIsOpen(false);
    setSelectedUsers([]);
  };

  const onCancel = () => {
    setIsOpen(false);
    setSelectedUsers([]);
  };

  const onDelete = (uid: string) => {
    const newUsers = selectedUsers.filter((u) => u.uid !== uid) || [];
    setSelectedUsers(newUsers);
  };

  const onSubmit = async () => {
    const userIds = selectedUsers.map((user) => user.uid);
    const data: ProjectUserCreateBody = {
      projectId: props.projectUid,
      userIds,
    };
    setIsLoading(true);
    const result = await post(apiPath(`/v1/project_users/create`), {
      data,
    });
    if (!result.isSuccess) {
      setIsLoading(false);
      return;
    }
    props.onRequested(selectedUsers);
    setIsLoading(false);
    setIsOpen(false);
    setSelectedUsers([]);
  };

  // テキストフィールドの入力値に変更があった時に呼ばれるcallback
  const onInputChange = async (e: SyntheticEvent, value: string) => {
    if (e?.type === 'click') {
      // optionのclickを起因だった場合、テキストフィールドを空文字にする
      setInputValue('');
    } else {
      setInputValue(value);
    }
  };

  // オプションを選択した時/選択済みラベルを削除に呼ばれるcallback
  const onChangeAutoComplete = (
    e: SyntheticEvent,
    value: { uid: string; title: string; image: null | string }[] | null,
  ) => {
    if (Array.isArray(value)) {
      const newSelectedUsers = value.map((v) => {
        return {
          uid: v.uid,
          name: v.title,
          profileImage: v.image,
        };
      });
      setSelectedUsers(newSelectedUsers);
    }
  };

  const projectUsers = props?.project?.users ?? [];
  const selectedProjectUserIds = selectedUsers.map((u) => u.uid);
  const options: {
    uid: string;
    title: string;
    image: string;
  }[] = suggestTeamUsers
    .filter(
      (user) =>
        !selectedProjectUserIds.includes(user.userId) &&
        projectUsers.every((u) => u.uid !== user.userId) &&
        user.userId !== props.viewer.uid,
    )
    .map((user) => ({
      uid: user.userId,
      title: user.name,
      image: user.profileImage ?? '',
    }));

  return {
    state: {
      isOpen,
      isLoading: isLoading,
      selectedUsers,
      options,
      inputValue,
    },
    actions: {
      setIsOpen,
      onClose,
      onCancel,
      onSubmit,
      onDelete,
      onInputChange,
      onChangeAutoComplete,
    },
  };
}
